import { useEffect, useState } from 'react';

// prettier-ignore
export type ISO3166Alpha2Code = 'AF' | 'AX' | 'AL' | 'DZ' | 'AS' | 'AD' | 'AO' | 'AI' | 'AQ' | 'AG' | 'AR' | 'AM' | 'AW' | 'AU' | 'AT' | 'AZ' | 'BS' | 'BH' | 'BD' | 'BB' | 'BY' | 'BE' | 'BZ' | 'BJ' | 'BM' | 'BT' | 'BO' | 'BQ' | 'BA' | 'BW' | 'BV' | 'BR' | 'IO' | 'BN' | 'BG' | 'BF' | 'BI' | 'CV' | 'KH' | 'CM' | 'CA' | 'KY' | 'CF' | 'TD' | 'CL' | 'CN' | 'CX' | 'CC' | 'CO' | 'KM' | 'CD' | 'CG' | 'CK' | 'CR' | 'CI' | 'HR' | 'CU' | 'CW' | 'CY' | 'CZ' | 'DK' | 'DJ' | 'DM' | 'DO' | 'EC' | 'EG' | 'SV' | 'GQ' | 'ER' | 'EE' | 'SZ' | 'ET' | 'FK' | 'FO' | 'FJ' | 'FI' | 'FR' | 'GF' | 'PF' | 'TF' | 'GA' | 'GM' | 'GE' | 'DE' | 'GH' | 'GI' | 'GR' | 'GL' | 'GD' | 'GP' | 'GU' | 'GT' | 'GG' | 'GN' | 'GW' | 'GY' | 'HT' | 'HM' | 'VA' | 'HN' | 'HK' | 'HU' | 'IS' | 'IN' | 'ID' | 'IR' | 'IQ' | 'IE' | 'IM' | 'IL' | 'IT' | 'JM' | 'JP' | 'JE' | 'JO' | 'KZ' | 'KE' | 'KI' | 'KP' | 'KR' | 'KW' | 'KG' | 'LA' | 'LV' | 'LB' | 'LS' | 'LR' | 'LY' | 'LI' | 'LT' | 'LU' | 'MO' | 'MK' | 'MG' | 'MW' | 'MY' | 'MV' | 'ML' | 'MT' | 'MH' | 'MQ' | 'MR' | 'MU' | 'YT' | 'MX' | 'FM' | 'MD' | 'MC' | 'MN' | 'ME' | 'MS' | 'MA' | 'MZ' | 'MM' | 'NA' | 'NR' | 'NP' | 'NL' | 'NC' | 'NZ' | 'NI' | 'NE' | 'NG' | 'NU' | 'NF' | 'MP' | 'NO' | 'OM' | 'PK' | 'PW' | 'PS' | 'PA' | 'PG' | 'PY' | 'PE' | 'PH' | 'PN' | 'PL' | 'PT' | 'PR' | 'QA' | 'RE' | 'RO' | 'RU' | 'RW' | 'BL' | 'SH' | 'KN' | 'LC' | 'MF' | 'PM' | 'VC' | 'WS' | 'SM' | 'ST' | 'SA' | 'SN' | 'RS' | 'SC' | 'SL' | 'SG' | 'SX' | 'SK' | 'SI' | 'SB' | 'SO' | 'ZA' | 'GS' | 'SS' | 'ES' | 'LK' | 'SD' | 'SR' | 'SJ' | 'SE' | 'CH' | 'SY' | 'TW' | 'TJ' | 'TZ' | 'TH' | 'TL' | 'TG' | 'TK' | 'TO' | 'TT' | 'TN' | 'TR' | 'TM' | 'TC' | 'TV' | 'UG' | 'UA' | 'AE' | 'GB' | 'UM' | 'US' | 'UY' | 'UZ' | 'VU' | 'VE' | 'VN' | 'VG' | 'VI' | 'WF' | 'EH' | 'YE' | 'ZM' | 'ZW';

/**
 * Shows a circular flag icon for a given country based on a given ISO 3166-1 alpha 2 code (https://en.wikipedia.org/wiki/ISO_3166-1)
 */
export const CountryIcon = ({
  alpha2,
  height,
}: {
  alpha2?: ISO3166Alpha2Code;
  height?: number;
}) => {
  const [svgUrl, setSvgUrl] = useState();
  // Ensure code is uppercase to match the SVG file name
  const upperCode = alpha2 ? alpha2.toUpperCase() : 'earth';

  /** Dynamically import the SVG based on the alpha2 code.
   * Ideally we would import these via a URL instead of dynamic code, but that would require
   * modifying our build to copy all static icon assets to a known location.
   */
  useEffect(() => {
    const loadSvg = async () => {
      try {
        // Dynamically import the SVG with ?url suffix
        const svgModule = await import(`./country-icons/${upperCode}.svg?url`);
        setSvgUrl(svgModule.default);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        // No output
      }
    };
    loadSvg();
  }, [upperCode]);
  return <img src={svgUrl} style={{ height: height ?? undefined }} />;
};

type ISO3166CountryData = {
  alpha2: ISO3166Alpha2Code;
  alpha3: string;
  countryName: string;
  /** These are numbers, but the source data set specified them as
   * three digit number strings, so that has been preserved here */
  numeric: string;
};

/** A full listing of ISO3166 info. Only currently used when listing all flag icons in the
 * Storybook story.*/
export const alpha2ToIso3166Data: Record<ISO3166Alpha2Code, ISO3166CountryData> = {
  AF: {
    alpha2: 'AF',
    alpha3: 'AFG',
    countryName: 'Afghanistan',
    numeric: '004',
  },
  AX: {
    alpha2: 'AX',
    alpha3: 'ALA',
    countryName: 'Åland Islands',
    numeric: '248',
  },
  AL: {
    alpha2: 'AL',
    alpha3: 'ALB',
    countryName: 'Albania',
    numeric: '008',
  },
  DZ: {
    alpha2: 'DZ',
    alpha3: 'DZA',
    countryName: 'Algeria',
    numeric: '012',
  },
  AS: {
    alpha2: 'AS',
    alpha3: 'ASM',
    countryName: 'American Samoa',
    numeric: '016',
  },
  AD: {
    alpha2: 'AD',
    alpha3: 'AND',
    countryName: 'Andorra',
    numeric: '020',
  },
  AO: {
    alpha2: 'AO',
    alpha3: 'AGO',
    countryName: 'Angola',
    numeric: '024',
  },
  AI: {
    alpha2: 'AI',
    alpha3: 'AIA',
    countryName: 'Anguilla',
    numeric: '660',
  },
  AQ: {
    alpha2: 'AQ',
    alpha3: 'ATA',
    countryName: 'Antarctica',
    numeric: '010',
  },
  AG: {
    alpha2: 'AG',
    alpha3: 'ATG',
    countryName: 'Antigua and Barbuda',
    numeric: '028',
  },
  AR: {
    alpha2: 'AR',
    alpha3: 'ARG',
    countryName: 'Argentina',
    numeric: '032',
  },
  AM: {
    alpha2: 'AM',
    alpha3: 'ARM',
    countryName: 'Armenia',
    numeric: '051',
  },
  AW: {
    alpha2: 'AW',
    alpha3: 'ABW',
    countryName: 'Aruba',
    numeric: '533',
  },
  AU: {
    alpha2: 'AU',
    alpha3: 'AUS',
    countryName: 'Australia',
    numeric: '036',
  },
  AT: {
    alpha2: 'AT',
    alpha3: 'AUT',
    countryName: 'Austria',
    numeric: '040',
  },
  AZ: {
    alpha2: 'AZ',
    alpha3: 'AZE',
    countryName: 'Azerbaijan',
    numeric: '031',
  },
  BS: {
    alpha2: 'BS',
    alpha3: 'BHS',
    countryName: 'Bahamas (the)',
    numeric: '044',
  },
  BH: {
    alpha2: 'BH',
    alpha3: 'BHR',
    countryName: 'Bahrain',
    numeric: '048',
  },
  BD: {
    alpha2: 'BD',
    alpha3: 'BGD',
    countryName: 'Bangladesh',
    numeric: '050',
  },
  BB: {
    alpha2: 'BB',
    alpha3: 'BRB',
    countryName: 'Barbados',
    numeric: '052',
  },
  BY: {
    alpha2: 'BY',
    alpha3: 'BLR',
    countryName: 'Belarus',
    numeric: '112',
  },
  BE: {
    alpha2: 'BE',
    alpha3: 'BEL',
    countryName: 'Belgium',
    numeric: '056',
  },
  BZ: {
    alpha2: 'BZ',
    alpha3: 'BLZ',
    countryName: 'Belize',
    numeric: '084',
  },
  BJ: {
    alpha2: 'BJ',
    alpha3: 'BEN',
    countryName: 'Benin',
    numeric: '204',
  },
  BM: {
    alpha2: 'BM',
    alpha3: 'BMU',
    countryName: 'Bermuda',
    numeric: '060',
  },
  BT: {
    alpha2: 'BT',
    alpha3: 'BTN',
    countryName: 'Bhutan',
    numeric: '064',
  },
  BO: {
    alpha2: 'BO',
    alpha3: 'BOL',
    countryName: 'Bolivia (Plurinational State of)',
    numeric: '068',
  },
  BQ: {
    alpha2: 'BQ',
    alpha3: 'BES',
    countryName: 'Bonaire, Sint Eustatius and Saba',
    numeric: '535',
  },
  BA: {
    alpha2: 'BA',
    alpha3: 'BIH',
    countryName: 'Bosnia and Herzegovina',
    numeric: '070',
  },
  BW: {
    alpha2: 'BW',
    alpha3: 'BWA',
    countryName: 'Botswana',
    numeric: '072',
  },
  BV: {
    alpha2: 'BV',
    alpha3: 'BVT',
    countryName: 'Bouvet Island',
    numeric: '074',
  },
  BR: {
    alpha2: 'BR',
    alpha3: 'BRA',
    countryName: 'Brazil',
    numeric: '076',
  },
  IO: {
    alpha2: 'IO',
    alpha3: 'IOT',
    countryName: 'British Indian Ocean Territory (the)',
    numeric: '086',
  },
  BN: {
    alpha2: 'BN',
    alpha3: 'BRN',
    countryName: 'Brunei Darussalam',
    numeric: '096',
  },
  BG: {
    alpha2: 'BG',
    alpha3: 'BGR',
    countryName: 'Bulgaria',
    numeric: '100',
  },
  BF: {
    alpha2: 'BF',
    alpha3: 'BFA',
    countryName: 'Burkina Faso',
    numeric: '854',
  },
  BI: {
    alpha2: 'BI',
    alpha3: 'BDI',
    countryName: 'Burundi',
    numeric: '108',
  },
  CV: {
    alpha2: 'CV',
    alpha3: 'CPV',
    countryName: 'Cabo Verde',
    numeric: '132',
  },
  KH: {
    alpha2: 'KH',
    alpha3: 'KHM',
    countryName: 'Cambodia',
    numeric: '116',
  },
  CM: {
    alpha2: 'CM',
    alpha3: 'CMR',
    countryName: 'Cameroon',
    numeric: '120',
  },
  CA: {
    alpha2: 'CA',
    alpha3: 'CAN',
    countryName: 'Canada',
    numeric: '124',
  },
  KY: {
    alpha2: 'KY',
    alpha3: 'CYM',
    countryName: 'Cayman Islands (the)',
    numeric: '136',
  },
  CF: {
    alpha2: 'CF',
    alpha3: 'CAF',
    countryName: 'Central African Republic (the)',
    numeric: '140',
  },
  TD: {
    alpha2: 'TD',
    alpha3: 'TCD',
    countryName: 'Chad',
    numeric: '148',
  },
  CL: {
    alpha2: 'CL',
    alpha3: 'CHL',
    countryName: 'Chile',
    numeric: '152',
  },
  CN: {
    alpha2: 'CN',
    alpha3: 'CHN',
    countryName: 'China',
    numeric: '156',
  },
  CX: {
    alpha2: 'CX',
    alpha3: 'CXR',
    countryName: 'Christmas Island',
    numeric: '162',
  },
  CC: {
    alpha2: 'CC',
    alpha3: 'CCK',
    countryName: 'Cocos (Keeling) Islands (the)',
    numeric: '166',
  },
  CO: {
    alpha2: 'CO',
    alpha3: 'COL',
    countryName: 'Colombia',
    numeric: '170',
  },
  KM: {
    alpha2: 'KM',
    alpha3: 'COM',
    countryName: 'Comoros (the)',
    numeric: '174',
  },
  CD: {
    alpha2: 'CD',
    alpha3: 'COD',
    countryName: 'Congo (the Democratic Republic of the)',
    numeric: '180',
  },
  CG: {
    alpha2: 'CG',
    alpha3: 'COG',
    countryName: 'Congo (the)',
    numeric: '178',
  },
  CK: {
    alpha2: 'CK',
    alpha3: 'COK',
    countryName: 'Cook Islands (the)',
    numeric: '184',
  },
  CR: {
    alpha2: 'CR',
    alpha3: 'CRI',
    countryName: 'Costa Rica',
    numeric: '188',
  },
  CI: {
    alpha2: 'CI',
    alpha3: 'CIV',
    countryName: "Côte d'Ivoire",
    numeric: '384',
  },
  HR: {
    alpha2: 'HR',
    alpha3: 'HRV',
    countryName: 'Croatia',
    numeric: '191',
  },
  CU: {
    alpha2: 'CU',
    alpha3: 'CUB',
    countryName: 'Cuba',
    numeric: '192',
  },
  CW: {
    alpha2: 'CW',
    alpha3: 'CUW',
    countryName: 'Curaçao',
    numeric: '531',
  },
  CY: {
    alpha2: 'CY',
    alpha3: 'CYP',
    countryName: 'Cyprus',
    numeric: '196',
  },
  CZ: {
    alpha2: 'CZ',
    alpha3: 'CZE',
    countryName: 'Czechia',
    numeric: '203',
  },
  DK: {
    alpha2: 'DK',
    alpha3: 'DNK',
    countryName: 'Denmark',
    numeric: '208',
  },
  DJ: {
    alpha2: 'DJ',
    alpha3: 'DJI',
    countryName: 'Djibouti',
    numeric: '262',
  },
  DM: {
    alpha2: 'DM',
    alpha3: 'DMA',
    countryName: 'Dominica',
    numeric: '212',
  },
  DO: {
    alpha2: 'DO',
    alpha3: 'DOM',
    countryName: 'Dominican Republic (the)',
    numeric: '214',
  },
  EC: {
    alpha2: 'EC',
    alpha3: 'ECU',
    countryName: 'Ecuador',
    numeric: '218',
  },
  EG: {
    alpha2: 'EG',
    alpha3: 'EGY',
    countryName: 'Egypt',
    numeric: '818',
  },
  SV: {
    alpha2: 'SV',
    alpha3: 'SLV',
    countryName: 'El Salvador',
    numeric: '222',
  },
  GQ: {
    alpha2: 'GQ',
    alpha3: 'GNQ',
    countryName: 'Equatorial Guinea',
    numeric: '226',
  },
  ER: {
    alpha2: 'ER',
    alpha3: 'ERI',
    countryName: 'Eritrea',
    numeric: '232',
  },
  EE: {
    alpha2: 'EE',
    alpha3: 'EST',
    countryName: 'Estonia',
    numeric: '233',
  },
  SZ: {
    alpha2: 'SZ',
    alpha3: 'SWZ',
    countryName: 'Eswatini',
    numeric: '748',
  },
  ET: {
    alpha2: 'ET',
    alpha3: 'ETH',
    countryName: 'Ethiopia',
    numeric: '231',
  },
  FK: {
    alpha2: 'FK',
    alpha3: 'FLK',
    countryName: 'Falkland Islands (the) [Malvinas]',
    numeric: '238',
  },
  FO: {
    alpha2: 'FO',
    alpha3: 'FRO',
    countryName: 'Faroe Islands (the)',
    numeric: '234',
  },
  FJ: {
    alpha2: 'FJ',
    alpha3: 'FJI',
    countryName: 'Fiji',
    numeric: '242',
  },
  FI: {
    alpha2: 'FI',
    alpha3: 'FIN',
    countryName: 'Finland',
    numeric: '246',
  },
  FR: {
    alpha2: 'FR',
    alpha3: 'FRA',
    countryName: 'France',
    numeric: '250',
  },
  GF: {
    alpha2: 'GF',
    alpha3: 'GUF',
    countryName: 'French Guiana',
    numeric: '254',
  },
  PF: {
    alpha2: 'PF',
    alpha3: 'PYF',
    countryName: 'French Polynesia',
    numeric: '258',
  },
  TF: {
    alpha2: 'TF',
    alpha3: 'ATF',
    countryName: 'French Southern Territories (the)',
    numeric: '260',
  },
  GA: {
    alpha2: 'GA',
    alpha3: 'GAB',
    countryName: 'Gabon',
    numeric: '266',
  },
  GM: {
    alpha2: 'GM',
    alpha3: 'GMB',
    countryName: 'Gambia (the)',
    numeric: '270',
  },
  GE: {
    alpha2: 'GE',
    alpha3: 'GEO',
    countryName: 'Georgia',
    numeric: '268',
  },
  DE: {
    alpha2: 'DE',
    alpha3: 'DEU',
    countryName: 'Germany',
    numeric: '276',
  },
  GH: {
    alpha2: 'GH',
    alpha3: 'GHA',
    countryName: 'Ghana',
    numeric: '288',
  },
  GI: {
    alpha2: 'GI',
    alpha3: 'GIB',
    countryName: 'Gibraltar',
    numeric: '292',
  },
  GR: {
    alpha2: 'GR',
    alpha3: 'GRC',
    countryName: 'Greece',
    numeric: '300',
  },
  GL: {
    alpha2: 'GL',
    alpha3: 'GRL',
    countryName: 'Greenland',
    numeric: '304',
  },
  GD: {
    alpha2: 'GD',
    alpha3: 'GRD',
    countryName: 'Grenada',
    numeric: '308',
  },
  GP: {
    alpha2: 'GP',
    alpha3: 'GLP',
    countryName: 'Guadeloupe',
    numeric: '312',
  },
  GU: {
    alpha2: 'GU',
    alpha3: 'GUM',
    countryName: 'Guam',
    numeric: '316',
  },
  GT: {
    alpha2: 'GT',
    alpha3: 'GTM',
    countryName: 'Guatemala',
    numeric: '320',
  },
  GG: {
    alpha2: 'GG',
    alpha3: 'GGY',
    countryName: 'Guernsey',
    numeric: '831',
  },
  GN: {
    alpha2: 'GN',
    alpha3: 'GIN',
    countryName: 'Guinea',
    numeric: '324',
  },
  GW: {
    alpha2: 'GW',
    alpha3: 'GNB',
    countryName: 'Guinea-Bissau',
    numeric: '624',
  },
  GY: {
    alpha2: 'GY',
    alpha3: 'GUY',
    countryName: 'Guyana',
    numeric: '328',
  },
  HT: {
    alpha2: 'HT',
    alpha3: 'HTI',
    countryName: 'Haiti',
    numeric: '332',
  },
  HM: {
    alpha2: 'HM',
    alpha3: 'HMD',
    countryName: 'Heard Island and McDonald Islands',
    numeric: '334',
  },
  VA: {
    alpha2: 'VA',
    alpha3: 'VAT',
    countryName: 'Holy See (the)',
    numeric: '336',
  },
  HN: {
    alpha2: 'HN',
    alpha3: 'HND',
    countryName: 'Honduras',
    numeric: '340',
  },
  HK: {
    alpha2: 'HK',
    alpha3: 'HKG',
    countryName: 'Hong Kong',
    numeric: '344',
  },
  HU: {
    alpha2: 'HU',
    alpha3: 'HUN',
    countryName: 'Hungary',
    numeric: '348',
  },
  IS: {
    alpha2: 'IS',
    alpha3: 'ISL',
    countryName: 'Iceland',
    numeric: '352',
  },
  IN: {
    alpha2: 'IN',
    alpha3: 'IND',
    countryName: 'India',
    numeric: '356',
  },
  ID: {
    alpha2: 'ID',
    alpha3: 'IDN',
    countryName: 'Indonesia',
    numeric: '360',
  },
  IR: {
    alpha2: 'IR',
    alpha3: 'IRN',
    countryName: 'Iran (Islamic Republic of)',
    numeric: '364',
  },
  IQ: {
    alpha2: 'IQ',
    alpha3: 'IRQ',
    countryName: 'Iraq',
    numeric: '368',
  },
  IE: {
    alpha2: 'IE',
    alpha3: 'IRL',
    countryName: 'Ireland',
    numeric: '372',
  },
  IM: {
    alpha2: 'IM',
    alpha3: 'IMN',
    countryName: 'Isle of Man',
    numeric: '833',
  },
  IL: {
    alpha2: 'IL',
    alpha3: 'ISR',
    countryName: 'Israel',
    numeric: '376',
  },
  IT: {
    alpha2: 'IT',
    alpha3: 'ITA',
    countryName: 'Italy',
    numeric: '380',
  },
  JM: {
    alpha2: 'JM',
    alpha3: 'JAM',
    countryName: 'Jamaica',
    numeric: '388',
  },
  JP: {
    alpha2: 'JP',
    alpha3: 'JPN',
    countryName: 'Japan',
    numeric: '392',
  },
  JE: {
    alpha2: 'JE',
    alpha3: 'JEY',
    countryName: 'Jersey',
    numeric: '832',
  },
  JO: {
    alpha2: 'JO',
    alpha3: 'JOR',
    countryName: 'Jordan',
    numeric: '400',
  },
  KZ: {
    alpha2: 'KZ',
    alpha3: 'KAZ',
    countryName: 'Kazakhstan',
    numeric: '398',
  },
  KE: {
    alpha2: 'KE',
    alpha3: 'KEN',
    countryName: 'Kenya',
    numeric: '404',
  },
  KI: {
    alpha2: 'KI',
    alpha3: 'KIR',
    countryName: 'Kiribati',
    numeric: '296',
  },
  KP: {
    alpha2: 'KP',
    alpha3: 'PRK',
    countryName: "Korea (the Democratic People's Republic of)",
    numeric: '408',
  },
  KR: {
    alpha2: 'KR',
    alpha3: 'KOR',
    countryName: 'Korea (the Republic of)',
    numeric: '410',
  },
  KW: {
    alpha2: 'KW',
    alpha3: 'KWT',
    countryName: 'Kuwait',
    numeric: '414',
  },
  KG: {
    alpha2: 'KG',
    alpha3: 'KGZ',
    countryName: 'Kyrgyzstan',
    numeric: '417',
  },
  LA: {
    alpha2: 'LA',
    alpha3: 'LAO',
    countryName: "Lao People's Democratic Republic (the)",
    numeric: '418',
  },
  LV: {
    alpha2: 'LV',
    alpha3: 'LVA',
    countryName: 'Latvia',
    numeric: '428',
  },
  LB: {
    alpha2: 'LB',
    alpha3: 'LBN',
    countryName: 'Lebanon',
    numeric: '422',
  },
  LS: {
    alpha2: 'LS',
    alpha3: 'LSO',
    countryName: 'Lesotho',
    numeric: '426',
  },
  LR: {
    alpha2: 'LR',
    alpha3: 'LBR',
    countryName: 'Liberia',
    numeric: '430',
  },
  LY: {
    alpha2: 'LY',
    alpha3: 'LBY',
    countryName: 'Libya',
    numeric: '434',
  },
  LI: {
    alpha2: 'LI',
    alpha3: 'LIE',
    countryName: 'Liechtenstein',
    numeric: '438',
  },
  LT: {
    alpha2: 'LT',
    alpha3: 'LTU',
    countryName: 'Lithuania',
    numeric: '440',
  },
  LU: {
    alpha2: 'LU',
    alpha3: 'LUX',
    countryName: 'Luxembourg',
    numeric: '442',
  },
  MO: {
    alpha2: 'MO',
    alpha3: 'MAC',
    countryName: 'Macao',
    numeric: '446',
  },
  MK: {
    alpha2: 'MK',
    alpha3: 'MKD',
    countryName: 'Republic of North Macedonia',
    numeric: '807',
  },
  MG: {
    alpha2: 'MG',
    alpha3: 'MDG',
    countryName: 'Madagascar',
    numeric: '450',
  },
  MW: {
    alpha2: 'MW',
    alpha3: 'MWI',
    countryName: 'Malawi',
    numeric: '454',
  },
  MY: {
    alpha2: 'MY',
    alpha3: 'MYS',
    countryName: 'Malaysia',
    numeric: '458',
  },
  MV: {
    alpha2: 'MV',
    alpha3: 'MDV',
    countryName: 'Maldives',
    numeric: '462',
  },
  ML: {
    alpha2: 'ML',
    alpha3: 'MLI',
    countryName: 'Mali',
    numeric: '466',
  },
  MT: {
    alpha2: 'MT',
    alpha3: 'MLT',
    countryName: 'Malta',
    numeric: '470',
  },
  MH: {
    alpha2: 'MH',
    alpha3: 'MHL',
    countryName: 'Marshall Islands (the)',
    numeric: '584',
  },
  MQ: {
    alpha2: 'MQ',
    alpha3: 'MTQ',
    countryName: 'Martinique',
    numeric: '474',
  },
  MR: {
    alpha2: 'MR',
    alpha3: 'MRT',
    countryName: 'Mauritania',
    numeric: '478',
  },
  MU: {
    alpha2: 'MU',
    alpha3: 'MUS',
    countryName: 'Mauritius',
    numeric: '480',
  },
  YT: {
    alpha2: 'YT',
    alpha3: 'MYT',
    countryName: 'Mayotte',
    numeric: '175',
  },
  MX: {
    alpha2: 'MX',
    alpha3: 'MEX',
    countryName: 'Mexico',
    numeric: '484',
  },
  FM: {
    alpha2: 'FM',
    alpha3: 'FSM',
    countryName: 'Micronesia (Federated States of)',
    numeric: '583',
  },
  MD: {
    alpha2: 'MD',
    alpha3: 'MDA',
    countryName: 'Moldova (the Republic of)',
    numeric: '498',
  },
  MC: {
    alpha2: 'MC',
    alpha3: 'MCO',
    countryName: 'Monaco',
    numeric: '492',
  },
  MN: {
    alpha2: 'MN',
    alpha3: 'MNG',
    countryName: 'Mongolia',
    numeric: '496',
  },
  ME: {
    alpha2: 'ME',
    alpha3: 'MNE',
    countryName: 'Montenegro',
    numeric: '499',
  },
  MS: {
    alpha2: 'MS',
    alpha3: 'MSR',
    countryName: 'Montserrat',
    numeric: '500',
  },
  MA: {
    alpha2: 'MA',
    alpha3: 'MAR',
    countryName: 'Morocco',
    numeric: '504',
  },
  MZ: {
    alpha2: 'MZ',
    alpha3: 'MOZ',
    countryName: 'Mozambique',
    numeric: '508',
  },
  MM: {
    alpha2: 'MM',
    alpha3: 'MMR',
    countryName: 'Myanmar',
    numeric: '104',
  },
  NA: {
    alpha2: 'NA',
    alpha3: 'NAM',
    countryName: 'Namibia',
    numeric: '516',
  },
  NR: {
    alpha2: 'NR',
    alpha3: 'NRU',
    countryName: 'Nauru',
    numeric: '520',
  },
  NP: {
    alpha2: 'NP',
    alpha3: 'NPL',
    countryName: 'Nepal',
    numeric: '524',
  },
  NL: {
    alpha2: 'NL',
    alpha3: 'NLD',
    countryName: 'Netherlands (the)',
    numeric: '528',
  },
  NC: {
    alpha2: 'NC',
    alpha3: 'NCL',
    countryName: 'New Caledonia',
    numeric: '540',
  },
  NZ: {
    alpha2: 'NZ',
    alpha3: 'NZL',
    countryName: 'New Zealand',
    numeric: '554',
  },
  NI: {
    alpha2: 'NI',
    alpha3: 'NIC',
    countryName: 'Nicaragua',
    numeric: '558',
  },
  NE: {
    alpha2: 'NE',
    alpha3: 'NER',
    countryName: 'Niger (the)',
    numeric: '562',
  },
  NG: {
    alpha2: 'NG',
    alpha3: 'NGA',
    countryName: 'Nigeria',
    numeric: '566',
  },
  NU: {
    alpha2: 'NU',
    alpha3: 'NIU',
    countryName: 'Niue',
    numeric: '570',
  },
  NF: {
    alpha2: 'NF',
    alpha3: 'NFK',
    countryName: 'Norfolk Island',
    numeric: '574',
  },
  MP: {
    alpha2: 'MP',
    alpha3: 'MNP',
    countryName: 'Northern Mariana Islands (the)',
    numeric: '580',
  },
  NO: {
    alpha2: 'NO',
    alpha3: 'NOR',
    countryName: 'Norway',
    numeric: '578',
  },
  OM: {
    alpha2: 'OM',
    alpha3: 'OMN',
    countryName: 'Oman',
    numeric: '512',
  },
  PK: {
    alpha2: 'PK',
    alpha3: 'PAK',
    countryName: 'Pakistan',
    numeric: '586',
  },
  PW: {
    alpha2: 'PW',
    alpha3: 'PLW',
    countryName: 'Palau',
    numeric: '585',
  },
  PS: {
    alpha2: 'PS',
    alpha3: 'PSE',
    countryName: 'Palestine, State of',
    numeric: '275',
  },
  PA: {
    alpha2: 'PA',
    alpha3: 'PAN',
    countryName: 'Panama',
    numeric: '591',
  },
  PG: {
    alpha2: 'PG',
    alpha3: 'PNG',
    countryName: 'Papua New Guinea',
    numeric: '598',
  },
  PY: {
    alpha2: 'PY',
    alpha3: 'PRY',
    countryName: 'Paraguay',
    numeric: '600',
  },
  PE: {
    alpha2: 'PE',
    alpha3: 'PER',
    countryName: 'Peru',
    numeric: '604',
  },
  PH: {
    alpha2: 'PH',
    alpha3: 'PHL',
    countryName: 'Philippines (the)',
    numeric: '608',
  },
  PN: {
    alpha2: 'PN',
    alpha3: 'PCN',
    countryName: 'Pitcairn',
    numeric: '612',
  },
  PL: {
    alpha2: 'PL',
    alpha3: 'POL',
    countryName: 'Poland',
    numeric: '616',
  },
  PT: {
    alpha2: 'PT',
    alpha3: 'PRT',
    countryName: 'Portugal',
    numeric: '620',
  },
  PR: {
    alpha2: 'PR',
    alpha3: 'PRI',
    countryName: 'Puerto Rico',
    numeric: '630',
  },
  QA: {
    alpha2: 'QA',
    alpha3: 'QAT',
    countryName: 'Qatar',
    numeric: '634',
  },
  RE: {
    alpha2: 'RE',
    alpha3: 'REU',
    countryName: 'Réunion',
    numeric: '638',
  },
  RO: {
    alpha2: 'RO',
    alpha3: 'ROU',
    countryName: 'Romania',
    numeric: '642',
  },
  RU: {
    alpha2: 'RU',
    alpha3: 'RUS',
    countryName: 'Russian Federation (the)',
    numeric: '643',
  },
  RW: {
    alpha2: 'RW',
    alpha3: 'RWA',
    countryName: 'Rwanda',
    numeric: '646',
  },
  BL: {
    alpha2: 'BL',
    alpha3: 'BLM',
    countryName: 'Saint Barthélemy',
    numeric: '652',
  },
  SH: {
    alpha2: 'SH',
    alpha3: 'SHN',
    countryName: 'Saint Helena, Ascension and Tristan da Cunha',
    numeric: '654',
  },
  KN: {
    alpha2: 'KN',
    alpha3: 'KNA',
    countryName: 'Saint Kitts and Nevis',
    numeric: '659',
  },
  LC: {
    alpha2: 'LC',
    alpha3: 'LCA',
    countryName: 'Saint Lucia',
    numeric: '662',
  },
  MF: {
    alpha2: 'MF',
    alpha3: 'MAF',
    countryName: 'Saint Martin (French part)',
    numeric: '663',
  },
  PM: {
    alpha2: 'PM',
    alpha3: 'SPM',
    countryName: 'Saint Pierre and Miquelon',
    numeric: '666',
  },
  VC: {
    alpha2: 'VC',
    alpha3: 'VCT',
    countryName: 'Saint Vincent and the Grenadines',
    numeric: '670',
  },
  WS: {
    alpha2: 'WS',
    alpha3: 'WSM',
    countryName: 'Samoa',
    numeric: '882',
  },
  SM: {
    alpha2: 'SM',
    alpha3: 'SMR',
    countryName: 'San Marino',
    numeric: '674',
  },
  ST: {
    alpha2: 'ST',
    alpha3: 'STP',
    countryName: 'Sao Tome and Principe',
    numeric: '678',
  },
  SA: {
    alpha2: 'SA',
    alpha3: 'SAU',
    countryName: 'Saudi Arabia',
    numeric: '682',
  },
  SN: {
    alpha2: 'SN',
    alpha3: 'SEN',
    countryName: 'Senegal',
    numeric: '686',
  },
  RS: {
    alpha2: 'RS',
    alpha3: 'SRB',
    countryName: 'Serbia',
    numeric: '688',
  },
  SC: {
    alpha2: 'SC',
    alpha3: 'SYC',
    countryName: 'Seychelles',
    numeric: '690',
  },
  SL: {
    alpha2: 'SL',
    alpha3: 'SLE',
    countryName: 'Sierra Leone',
    numeric: '694',
  },
  SG: {
    alpha2: 'SG',
    alpha3: 'SGP',
    countryName: 'Singapore',
    numeric: '702',
  },
  SX: {
    alpha2: 'SX',
    alpha3: 'SXM',
    countryName: 'Sint Maarten (Dutch part)',
    numeric: '534',
  },
  SK: {
    alpha2: 'SK',
    alpha3: 'SVK',
    countryName: 'Slovakia',
    numeric: '703',
  },
  SI: {
    alpha2: 'SI',
    alpha3: 'SVN',
    countryName: 'Slovenia',
    numeric: '705',
  },
  SB: {
    alpha2: 'SB',
    alpha3: 'SLB',
    countryName: 'Solomon Islands',
    numeric: '090',
  },
  SO: {
    alpha2: 'SO',
    alpha3: 'SOM',
    countryName: 'Somalia',
    numeric: '706',
  },
  ZA: {
    alpha2: 'ZA',
    alpha3: 'ZAF',
    countryName: 'South Africa',
    numeric: '710',
  },
  GS: {
    alpha2: 'GS',
    alpha3: 'SGS',
    countryName: 'South Georgia and the South Sandwich Islands',
    numeric: '239',
  },
  SS: {
    alpha2: 'SS',
    alpha3: 'SSD',
    countryName: 'South Sudan',
    numeric: '728',
  },
  ES: {
    alpha2: 'ES',
    alpha3: 'ESP',
    countryName: 'Spain',
    numeric: '724',
  },
  LK: {
    alpha2: 'LK',
    alpha3: 'LKA',
    countryName: 'Sri Lanka',
    numeric: '144',
  },
  SD: {
    alpha2: 'SD',
    alpha3: 'SDN',
    countryName: 'Sudan (the)',
    numeric: '729',
  },
  SR: {
    alpha2: 'SR',
    alpha3: 'SUR',
    countryName: 'Suriname',
    numeric: '740',
  },
  SJ: {
    alpha2: 'SJ',
    alpha3: 'SJM',
    countryName: 'Svalbard and Jan Mayen',
    numeric: '744',
  },
  SE: {
    alpha2: 'SE',
    alpha3: 'SWE',
    countryName: 'Sweden',
    numeric: '752',
  },
  CH: {
    alpha2: 'CH',
    alpha3: 'CHE',
    countryName: 'Switzerland',
    numeric: '756',
  },
  SY: {
    alpha2: 'SY',
    alpha3: 'SYR',
    countryName: 'Syrian Arab Republic',
    numeric: '760',
  },
  TW: {
    alpha2: 'TW',
    alpha3: 'TWN',
    countryName: 'Taiwan (Province of China)',
    numeric: '158',
  },
  TJ: {
    alpha2: 'TJ',
    alpha3: 'TJK',
    countryName: 'Tajikistan',
    numeric: '762',
  },
  TZ: {
    alpha2: 'TZ',
    alpha3: 'TZA',
    countryName: 'Tanzania, United Republic of',
    numeric: '834',
  },
  TH: {
    alpha2: 'TH',
    alpha3: 'THA',
    countryName: 'Thailand',
    numeric: '764',
  },
  TL: {
    alpha2: 'TL',
    alpha3: 'TLS',
    countryName: 'Timor-Leste',
    numeric: '626',
  },
  TG: {
    alpha2: 'TG',
    alpha3: 'TGO',
    countryName: 'Togo',
    numeric: '768',
  },
  TK: {
    alpha2: 'TK',
    alpha3: 'TKL',
    countryName: 'Tokelau',
    numeric: '772',
  },
  TO: {
    alpha2: 'TO',
    alpha3: 'TON',
    countryName: 'Tonga',
    numeric: '776',
  },
  TT: {
    alpha2: 'TT',
    alpha3: 'TTO',
    countryName: 'Trinidad and Tobago',
    numeric: '780',
  },
  TN: {
    alpha2: 'TN',
    alpha3: 'TUN',
    countryName: 'Tunisia',
    numeric: '788',
  },
  TR: {
    alpha2: 'TR',
    alpha3: 'TUR',
    countryName: 'Turkey',
    numeric: '792',
  },
  TM: {
    alpha2: 'TM',
    alpha3: 'TKM',
    countryName: 'Turkmenistan',
    numeric: '795',
  },
  TC: {
    alpha2: 'TC',
    alpha3: 'TCA',
    countryName: 'Turks and Caicos Islands (the)',
    numeric: '796',
  },
  TV: {
    alpha2: 'TV',
    alpha3: 'TUV',
    countryName: 'Tuvalu',
    numeric: '798',
  },
  UG: {
    alpha2: 'UG',
    alpha3: 'UGA',
    countryName: 'Uganda',
    numeric: '800',
  },
  UA: {
    alpha2: 'UA',
    alpha3: 'UKR',
    countryName: 'Ukraine',
    numeric: '804',
  },
  AE: {
    alpha2: 'AE',
    alpha3: 'ARE',
    countryName: 'United Arab Emirates (the)',
    numeric: '784',
  },
  GB: {
    alpha2: 'GB',
    alpha3: 'GBR',
    countryName: 'United Kingdom of Great Britain and Northern Ireland (the)',
    numeric: '826',
  },
  UM: {
    alpha2: 'UM',
    alpha3: 'UMI',
    countryName: 'United States Minor Outlying Islands (the)',
    numeric: '581',
  },
  US: {
    alpha2: 'US',
    alpha3: 'USA',
    countryName: 'United States of America (the)',
    numeric: '840',
  },
  UY: {
    alpha2: 'UY',
    alpha3: 'URY',
    countryName: 'Uruguay',
    numeric: '858',
  },
  UZ: {
    alpha2: 'UZ',
    alpha3: 'UZB',
    countryName: 'Uzbekistan',
    numeric: '860',
  },
  VU: {
    alpha2: 'VU',
    alpha3: 'VUT',
    countryName: 'Vanuatu',
    numeric: '548',
  },
  VE: {
    alpha2: 'VE',
    alpha3: 'VEN',
    countryName: 'Venezuela (Bolivarian Republic of)',
    numeric: '862',
  },
  VN: {
    alpha2: 'VN',
    alpha3: 'VNM',
    countryName: 'Viet Nam',
    numeric: '704',
  },
  VG: {
    alpha2: 'VG',
    alpha3: 'VGB',
    countryName: 'Virgin Islands (British)',
    numeric: '092',
  },
  VI: {
    alpha2: 'VI',
    alpha3: 'VIR',
    countryName: 'Virgin Islands (U.S.)',
    numeric: '850',
  },
  WF: {
    alpha2: 'WF',
    alpha3: 'WLF',
    countryName: 'Wallis and Futuna',
    numeric: '876',
  },
  EH: {
    alpha2: 'EH',
    alpha3: 'ESH',
    countryName: 'Western Sahara',
    numeric: '732',
  },
  YE: {
    alpha2: 'YE',
    alpha3: 'YEM',
    countryName: 'Yemen',
    numeric: '887',
  },
  ZM: {
    alpha2: 'ZM',
    alpha3: 'ZMB',
    countryName: 'Zambia',
    numeric: '894',
  },
  ZW: {
    alpha2: 'ZW',
    alpha3: 'ZWE',
    countryName: 'Zimbabwe',
    numeric: '716',
  },
};
